package com.essntl.features.itinerary.ui.edit_itinerary.components

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryEvent
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryNavigation
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryState
import com.essntl.features.itinerary.ui.edit_itinerary.rememberSortedItineraryServices
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.key.upDownSelection
import com.essntl.uikit.rememberEquals
import com.essntl.uikit.theme.esColorScheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

@Composable
fun ItineraryServicesConciergeColumn(
    state: EditItineraryState,
    onEvent: (EditItineraryEvent) -> Unit,
    onNavigation: (EditItineraryNavigation) -> Unit,
    isConcierge: Boolean,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    val lazyListState = rememberLazyListState()

    val sortedItineraryServicesList = state.rememberSortedItineraryServices(isConcierge = isConcierge)

    val itineraryServicesList by remember(sortedItineraryServicesList) {
        mutableStateOf(sortedItineraryServicesList)
    }

    val groupedItineraryService = remember(itineraryServicesList) {
        itineraryServicesList
            .toList()
            .sortedBy { it.startDate }
            .groupBy { it.startDate }
    }

    val scrollState = rememberScrollState()

    val isLoading = remember(state) { state.isLoading }

    Column(
        verticalArrangement = Arrangement.spacedBy(EsConstants.ListRowVerticalSpacing),
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(scrollState)
            .padding(vertical = EsConstants.SectionVerticalPadding),
    ) {
        if (isLoading)
            EsSpinner()
        else if (itineraryServicesList.isEmpty())
            ItineraryServicesEmpty()
        else
            groupedItineraryService.forEach { (date, dayItineraryServicesList) ->
                key("header-$date") {
                    ItineraryServicesGroupDateHeader(
                        date = date,
                        modifier = Modifier
                            .align(Alignment.Start)
                            .padding(EsConstants.SectionHorizontalPadding),
                    )
                }

                val groupedItineraryServiceList = remember(dayItineraryServicesList) {
                    dayItineraryServicesList
                        .groupBy { it.linkedId.orEmpty().ifEmpty { it.id } }
                        .toList()
                }

                groupedItineraryServiceList.forEach { (_, list) ->
                    Column(
                        modifier = Modifier
                            .padding(
                                horizontal = EsConstants.SectionHorizontalPadding,
                            )
                            .border(
                                width = 1.dp,
                                color = if (list.size > 1) esColorScheme.primary else Color.Transparent,
                                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                            )
                            .clip(
                                RoundedCornerShape(EsConstants.RoundedCornerSize),
                            ),
                    ) {
                        list.forEachIndexed { index, itineraryService ->
                            val isSelected = rememberEquals(
                                item = itineraryService.id,
                                selectedIds = setOf(state.clickedItineraryServiceId),
                            ) {
                                it
                            }

                            val focusRequester = remember { FocusRequester() }

                            key(itineraryService.id) {
                                ItineraryServiceItem(
                                    state = state,
                                    onEvent = onEvent,
                                    onNavigation = onNavigation,
                                    isDndHover = false,
                                    itineraryService = itineraryService,
                                    isSelected = isSelected,
                                    isConcierge = isConcierge,
                                    modifier = modifier
                                        .upDownSelection(
                                            focusRequester = focusRequester,
                                            isSelected = isSelected,
                                            list = sortedItineraryServicesList,
                                            index = index,
                                            onSelect = {
                                                onNavigation(EditItineraryNavigation.EditItineraryService(it))
                                            },
                                            lazyListState = lazyListState,
                                            scrollTo = {
                                                scope.launch {
                                                    lazyListState.animateScrollToItem(it)
                                                }
                                            },
                                        )
                                        .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                                )

                                LaunchedEffect(isSelected) {
                                    delay(100L)
                                    if (isSelected)
                                        focusRequester.requestFocus()
                                }
                            }
                        }
                    }
                }
            }
    }
}